import { ChangeEvent, useState } from 'react';
import { useParams } from 'react-router-dom';
import { UploadTypeEnum } from '../constants/UploadTypeEnum';
import { CreativeVideo } from '../../../../../types';
import { useCreativeTemplatesBuilderStore } from 'stores/CreativeTemplatesBuilderStore';
import { getErrorsText, Severity, usePushNotifications } from 'services/NotificationService';
import { useUploadVideoFromFile, useUploadVideoFromUrl, VideoSourceEnum } from 'api';

export const useVideoUploader = () => {
  const notify = usePushNotifications();
  const { id: articleGroupId } = useParams<{ id: string }>();
  const store = useCreativeTemplatesBuilderStore();
  const [uploadVideoFromUrl] = useUploadVideoFromUrl();
  const [uploadVideoFromFile] = useUploadVideoFromFile();

  const [videoSrc, setVideoSrc] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [uploadType, setUploadType] = useState<UploadTypeEnum>(UploadTypeEnum.URL);
  const [uploadParams, setUploadParams] = useState<{ url: string; title: string }>({ url: '', title: '' });

  const handleUploadTypeChange = (value: UploadTypeEnum) => setUploadType(value);

  const handleUploadParamsChange = (event: ChangeEvent<HTMLInputElement>) =>
    setUploadParams({ ...uploadParams, [event.target.name]: event.target.value });

  const handleUpload = async () => {
    if (uploadType === UploadTypeEnum.URL) await uploadFromUrl();
    if (uploadType === UploadTypeEnum.LOCAL_DISK) await uploadFromFile();
  };

  const uploadFromFile = async () => {
    try {
      setLoading(true);
      const { data } = await uploadVideoFromFile({
        variables: {
          articleGroupId,
          title: uploadParams.title,
          file: videoFile
        }
      });

      if (data?.uploadVideoFromFile?.errors) {
        const errorMsg = getErrorsText(data.uploadVideoFromFile.errors);
        throw new Error(errorMsg);
      }

      notify({
        title: 'Success',
        message: 'Video was successfully added to FB Ad account',
        severity: Severity.SUCCESS
      });

      store.setVideos([
        {
          id: data!.uploadVideoFromFile!.id,
          videoSource: VideoSourceEnum.ACCOUNT,
          loading: true
        } as CreativeVideo,
        ...store.videos
      ]);
    } catch (e) {
      notify({ title: 'Error', message: e.message, severity: Severity.ERROR });
    } finally {
      setLoading(false);
      store.setOpenVideoUploaderDialog(false);
    }
  };

  const uploadFromUrl = async () => {
    try {
      setLoading(true);
      const { data } = await uploadVideoFromUrl({
        variables: {
          articleGroupId,
          url: uploadParams.url,
          title: uploadParams.title
        }
      });

      if (data?.uploadVideoFromUrl?.errors) {
        const errorMsg = getErrorsText(data.uploadVideoFromUrl.errors);
        throw new Error(errorMsg);
      }

      notify({
        title: 'Success',
        message: 'Video was successfully added to FB Ad account',
        severity: Severity.SUCCESS
      });

      store.setVideos([
        {
          id: data!.uploadVideoFromUrl!.id,
          videoSource: VideoSourceEnum.ACCOUNT,
          loading: true
        } as CreativeVideo,
        ...store.videos
      ]);
    } catch (e) {
      notify({ title: 'Error', message: e.message, severity: Severity.ERROR });
    } finally {
      setLoading(false);
      store.setOpenVideoUploaderDialog(false);
    }
  };

  const resetSelection = () => setVideoSrc('');

  const onSelectFile = (files: File[] | null, url?: string) => {
    if (url) {
      setVideoSrc(url);
      return;
    }

    if (files) {
      const reader = new FileReader();
      reader.addEventListener('load', () => setVideoSrc(reader?.result?.toString() || ''));
      reader.readAsDataURL(files[0]);

      const file = new File([files[0]], 'creative_video.mp4', { type: 'video/mp4' });
      setVideoFile(file);
    }
  };

  return {
    loading,
    videoSrc,
    uploadType,
    uploadParams,
    onSelectFile,
    handleUpload,
    resetSelection,
    handleUploadParamsChange,
    handleUploadTypeChange
  };
};
